import { useState, useEffect, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { MenuItem, Select, FormControl, InputLabel, FormHelperText } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import type Ouvrage from "models/Ouvrage";

const regroupement = [
  { typeObjet: "RES", regroupement: "OEP" },
  { typeObjet: "SOU", regroupement: "OEP" },
  { typeObjet: "STP", regroupement: "OEP" },
  { typeObjet: "STR", regroupement: "OEP" },
  { typeObjet: "STT", regroupement: "OEP" },
  { typeObjet: "REG", regroupement: "OEP" },
  { typeObjet: "DES", regroupement: "OEP" },
  { typeObjet: "CPT", regroupement: "OEP" },
  { typeObjet: "PUI", regroupement: "OEP" },
  { typeObjet: "DO", regroupement: "DO" },
  { typeObjet: "EPU", regroupement: "EPU" },
  { typeObjet: "REF", regroupement: "PPEU" },
  { typeObjet: "REL", regroupement: "PPEU" },
  { typeObjet: "BOR", regroupement: "PPEU" },
  { typeObjet: "H2S", regroupement: "PPEU" },
  { typeObjet: "PDE", regroupement: "PPEU" },
];

function getOuvrageLabel(ouvrage: Ouvrage): string {
  const precisionDo = ouvrage.typeObjet === "DO" ? ` (${ouvrage.rue})` : "";
  return `${ouvrage.designation}${precisionDo}`;
}

interface OuvrageSelectProps {
  allOuvrages: Array<Ouvrage>;
  typeOuvrage: string;
  communeINSEE: string;
  setPosteTechnique: (s: string) => void;
  setOuvrageDescription: (s: string) => void;
  defaultOuvrage?: Ouvrage;
  readOnly?: boolean;
  notOuvrageTypeString?: string;
  error?: boolean;
  helperText?: string;
}

function OuvrageSelect({
  allOuvrages,
  typeOuvrage,
  communeINSEE,
  setPosteTechnique,
  setOuvrageDescription,
  defaultOuvrage,
  readOnly = false,
  notOuvrageTypeString,
  error,
  helperText = "",
}: Readonly<OuvrageSelectProps>): ReactNode {
  const [value, setValue] = useState<string>(defaultOuvrage?.posteTechnique ?? "");

  /** Lorsque la commune sélectionnée change, on met à jour la Regex de
   * filtre des postes techniques */
  const inseeRegex: RegExp = useMemo(() => {
    let insee = communeINSEE === "" ? ".*" : communeINSEE;
    if (insee.startsWith("67")) {
      insee = insee.substring(2, 5);
    }
    return new RegExp(`[0-9]-[0-9]+-${insee}-[a-z]-[a-z]+-[0-9]+`, "i");
  }, [communeINSEE]);

  const filterOuvrages = useCallback(
    (options: Array<Ouvrage>) => {
      function ouvrageFilter(ouvrage: Ouvrage): boolean {
        return (
          regroupement
            .filter((r) => r.regroupement === typeOuvrage)
            ?.some((o) => ouvrage.typeObjet === o.typeObjet) &&
          inseeRegex.test(ouvrage.posteTechnique)
        );
      }
      const filteredOptions = options.filter(ouvrageFilter);
      if (
        value === defaultOuvrage?.posteTechnique &&
        !filteredOptions.some((o) => o.posteTechnique === defaultOuvrage?.posteTechnique)
      ) {
        filteredOptions.push(defaultOuvrage);
      }
      return filteredOptions;
    },
    [value, defaultOuvrage, inseeRegex, typeOuvrage]
  );

  // Lorsque l'un des paramètres de filtrage change, on refiltre les ouvrages
  const filteredOuvrages = useMemo(
    () => filterOuvrages(allOuvrages),
    [filterOuvrages, allOuvrages]
  );

  /** Lorsque le filtre ou la valeur change, on vérifie que la valeur
   * sélectionnée est toujours valide */
  useEffect(() => {
    if (!readOnly && !filteredOuvrages.some((_o) => _o.posteTechnique === value)) {
      setPosteTechnique("");
      setOuvrageDescription("");
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setValue("");
    }
  }, [filteredOuvrages, readOnly, setOuvrageDescription, setPosteTechnique, value]);

  function getHelperText(): string {
    const firstHelperText: string =
      typeOuvrage === "" || communeINSEE === ""
        ? "Veuillez préciser une commune et un type d'ouvrage. "
        : "";
    return `${firstHelperText}${helperText ?? ""}`;
  }

  return (
    <FormControl
      error={error}
      disabled={
        !readOnly &&
        (typeOuvrage === "" ||
          typeOuvrage === notOuvrageTypeString ||
          communeINSEE === "" ||
          filteredOuvrages.length <= 0)
      }
      fullWidth>
      <InputLabel id="ouvrage">Ouvrage</InputLabel>
      <Select
        labelId="ouvrage"
        label="Ouvrage"
        variant="outlined"
        inputProps={{ readOnly }}
        value={value}
        onChange={(e: SelectChangeEvent<string>) => {
          const o = filteredOuvrages.find((_o) => _o.posteTechnique === e.target.value);
          setValue(e.target.value);
          setPosteTechnique(o?.posteTechnique ?? "");
          setOuvrageDescription(o?.designation ?? "");
        }}>
        {filteredOuvrages.map((ouvrage: Ouvrage) => (
          <MenuItem key={ouvrage.posteTechnique} value={ouvrage.posteTechnique}>
            {getOuvrageLabel(ouvrage)}
          </MenuItem>
        ))}
      </Select>
      {typeOuvrage === "" || communeINSEE === "" || helperText !== "" ? (
        <FormHelperText>{getHelperText()}</FormHelperText>
      ) : (
        filteredOuvrages.length <= 0 &&
        typeOuvrage !== notOuvrageTypeString && (
          <FormHelperText>Aucun ouvrage de ce type dans cette commune.</FormHelperText>
        )
      )}
    </FormControl>
  );
}

export default OuvrageSelect;
